import { useState } from "react";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import {
  getValidationSchema,
  initialValues as initialValuesBase,
} from "./config";
import { Box, Stack } from "@mui/material";
import { StyledFormicError } from "./styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { RequirementDomain } from "types/requirementDomain";
import {
  useAddRequirementDomainMutation,
  useUpdateRequirementDomainMutation,
} from "graphql/__generated__/hooks";
import { Modal } from "components/Modal";
import { useNotifications } from "hooks/useNotifications";
import { Messages, NotificationSeverity } from "helpers/constants";

export type Props = {
  open: boolean;
  onDismiss: () => void;
  onSaved: () => void;
  domain?: RequirementDomain | null;
  orgId?: string | null;
};

function normalizeColorHex(value?: string | null): string | null {
  const trimmed = (value || "").trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

const CreateOrEditDomainModal = ({
  open,
  onDismiss,
  onSaved,
  domain = null,
  orgId = null,
}: Props) => {
  const isEditMode = domain !== null;
  const [loading, setLoading] = useState(false);
  const [addDomain] = useAddRequirementDomainMutation();
  const [updateDomain] = useUpdateRequirementDomainMutation();
  const { notify } = useNotifications();

  const handleCreate = async (values: {
    name: string;
    description: string | null;
    code: string | null;
    sort_order: number | null;
    color_hex: string | null;
  }) => {
    setLoading(true);
    try {
      await addDomain({
        variables: {
          org_id: orgId || null,
          name: values.name,
          description: values.description,
          code: values.code,
          sort_order: values.sort_order,
          color_hex: values.color_hex,
        },
      });
      onSaved();
      notify({
        message: Messages.successDomainCreated,
        severity: NotificationSeverity.success,
      });
    } catch (error) {
      notify({
        message:
          error instanceof Error
            ? error.message
            : "Failed to create domain.",
        severity: NotificationSeverity.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (values: {
    name: string;
    description: string | null;
    code: string | null;
    sort_order: number | null;
    color_hex: string | null;
  }) => {
    if (!domain?.requirement_domain_id) {
      return;
    }
    setLoading(true);
    try {
      await updateDomain({
        variables: {
          id: domain.requirement_domain_id,
          name: values.name,
          description: values.description,
          code: values.code,
          sort_order: values.sort_order,
          color_hex: values.color_hex,
        },
      });
      onSaved();
      notify({
        message: Messages.successDomainUpdated,
        severity: NotificationSeverity.success,
      });
    } catch (error) {
      notify({
        message:
          error instanceof Error
            ? error.message
            : "Failed to update domain.",
        severity: NotificationSeverity.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const initialValues = isEditMode
    ? {
        name: domain?.name || "",
        description: domain?.description || "",
        code: domain?.code || "",
        sort_order:
          domain?.sort_order === null || domain?.sort_order === undefined
            ? ""
            : String(domain.sort_order),
        color_hex: domain?.color_hex || "",
      }
    : initialValuesBase;

  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const sortOrderRaw = String(values.sort_order ?? "").trim();
      const payload = {
        name: values.name.trim(),
        description: values.description.trim() || null,
        code: values.code.trim() || null,
        sort_order: sortOrderRaw === "" ? null : Number(sortOrderRaw),
        color_hex: normalizeColorHex(values.color_hex),
      };
      if (isEditMode) {
        handleEdit(payload).then();
      } else {
        handleCreate(payload).then();
      }
    },
  });

  const previewColor = normalizeColorHex(formik.values.color_hex);

  return (
    <Modal
      open={open}
      onClose={onDismiss}
      title={isEditMode ? domain?.name || "Edit domain" : "Create a new domain"}
      size="small"
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mt: 1 }}>
            <TextField
              variant="standard"
              onChange={formik.handleChange}
              margin="dense"
              fullWidth
              name="name"
              label="Name"
              type="text"
              id="name"
              value={formik.values.name}
            />
            <ErrorMessage name="name" component={StyledFormicError} />

            <TextField
              variant="standard"
              onChange={formik.handleChange}
              margin="dense"
              fullWidth
              name="code"
              label="Code"
              type="text"
              id="code"
              value={formik.values.code}
            />
            <ErrorMessage name="code" component={StyledFormicError} />

            <TextField
              variant="standard"
              onChange={formik.handleChange}
              margin="dense"
              fullWidth
              name="description"
              label="Description"
              type="text"
              id="description"
              multiline
              minRows={2}
              value={formik.values.description}
            />
            <ErrorMessage name="description" component={StyledFormicError} />

            <TextField
              variant="standard"
              onChange={formik.handleChange}
              margin="dense"
              fullWidth
              name="sort_order"
              label="Sort order"
              type="number"
              id="sort_order"
              value={formik.values.sort_order}
            />
            <ErrorMessage name="sort_order" component={StyledFormicError} />

            <Stack direction="row" spacing={1.5} alignItems="flex-end">
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="color_hex"
                label="Color hex"
                type="text"
                id="color_hex"
                placeholder="#AABBCC"
                value={formik.values.color_hex}
              />
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  mb: 1,
                  borderRadius: "4px",
                  border: "1px solid rgba(0,0,0,0.2)",
                  bgcolor: previewColor || "transparent",
                  flexShrink: 0,
                }}
              />
            </Stack>
            <ErrorMessage name="color_hex" component={StyledFormicError} />

            <Grid sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Box mr={2}>
                <Button variant="outlined" onClick={onDismiss}>
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Grid>
          </Box>
        </form>
      </FormikProvider>
    </Modal>
  );
};

export default CreateOrEditDomainModal;
